<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import { computed, inject, nextTick } from "vue";
import { usePdf } from "#/composables/usePdfMode";
import { vDragScroll } from "#/directives/vDragScroll";
import { runFlyAnimation } from "#/directives/vFlyTo";
import { vMuted } from "#/directives/vMuted";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import type { IGroupModifiers, ISelectModifiers } from "#/types/iCardProduct";
import { formatPrice, getLocaleName } from "#/utils/i18next";
import { liquidModifiersKey } from "./liquidModifiersContext";

const props = withDefaults(defineProps<{ group: IGroupModifiers; inline?: boolean; rows?: number }>(), { inline: false, rows: 2 });

const { t, i18next } = useTranslation();
const { isDesktop } = usePdf();
const { width } = useWindowSize();

const themeStore = useThemeStore();
const mainStore = useMainStore();

const ctx = inject(liquidModifiersKey);
if (!ctx) throw new Error("LiquidModifiersGroup: missing liquidModifiersKey provider");

const groupModifiers = computed<ISelectModifiers[]>(
	() => props.group?.group_modifiers?.filter((modifier) => !ctx.isHiddenModifier(modifier, props.group)) ?? [],
);
const isRequired = computed(() => !ctx.isGroupSatisfied(props.group));

const formatTilePrice = (price: number, isFree: boolean) => {
	const text = formatPrice(isFree ? 0 : price, mainStore.config_lib?.currency_symbol);
	if (!props.inline) return `+ ${text}`;
	return isFree ? text : `+${text}`;
};

const tiles = computed(() =>
	groupModifiers.value.map((modifier) => {
		const price = ctx.getModifierPrice(modifier);
		return {
			modifier,
			id: modifier.id,
			name: getLocaleName(modifier, i18next.language),
			priceText: formatTilePrice(price, price <= 0),
			isFree: price <= 0,
			image: modifier.image_link || themeStore.data?.fallback_image || "",
			video: modifier.video_link || "",
			hasCounter: ctx.shouldShowCounter(modifier, props.group),
		};
	}),
);

const columns = computed(() => (width.value >= 767 ? Math.max(3, Math.floor(width.value / 240)) : 2));
const tracks = computed(() => {
	const count = tiles.value.length;
	if (count <= columns.value || props.rows === 1) return count;
	return Math.max(columns.value, Math.ceil(count / 2));
});
const tilesClass = computed(() => ({
	cms_tiles_static: isDesktop.value,
	cms_tiles_overflow: props.inline && tracks.value > columns.value,
}));
const tilesStyle = computed(() => (props.inline ? { "--cms-columns": columns.value, "--cms-tracks": tracks.value } : undefined));

const getAmount = (modifier: ISelectModifiers) => ctx.getAmount(modifier, props.group);
const isSelected = (modifier: ISelectModifiers) => getAmount(modifier) > 0;

const flyToBar = async (source: HTMLElement | null | undefined) => {
	if (!props.inline || !source) return;
	await nextTick();
	const barItem = document.querySelector<HTMLElement>(`[data-liquid-bar-group="${props.group.id}"]`);
	const previews = barItem?.querySelectorAll<HTMLElement>(".cms_bar_preview");
	const target = previews?.length ? previews[previews.length - 1] : barItem?.querySelector<HTMLElement>(".cms_bar_image");
	if (target) runFlyAnimation(source, { target });
};

const getTileImage = (event: MouseEvent) => (event.currentTarget as HTMLElement).closest(".cms_tile")?.querySelector<HTMLElement>(".cms_tile_image");

const canIncrementTile = (modifier: ISelectModifiers) =>
	ctx.canIncrementModifier(modifier, props.group) && (!props.inline || ctx.isSupportsModifierQuantity(modifier, props.group));

const stopInlineCounterClick = (event: MouseEvent) => {
	if (props.inline) event.stopPropagation();
};

const selectTile = async (event: MouseEvent, modifier: ISelectModifiers) => {
	if (props.inline && isSelected(modifier)) {
		ctx.removeModifier(modifier, props.group);
		return;
	}
	const source = getTileImage(event);
	if (await ctx.selectModifier(modifier, props.group)) await flyToBar(source);
};

const incrementTile = async (event: MouseEvent, modifier: ISelectModifiers) => {
	const source = getTileImage(event);
	if (await ctx.incrementModifier(modifier, props.group)) await flyToBar(source);
};
</script>

<template>
	<section class="cms_group" :class="{ cms_group_required_empty: isRequired, cms_group_inline: inline }" :data-group-id="group.id">
		<header class="cms_group_header">
			<div class="cms_group_title">
				<span v-if="isRequired" class="cms_group_title_required_label"> {{ t(`card.modifiers.required`) }} </span>
				<span class="cms_group_title_text">{{ getLocaleName(group, i18next.language) }}</span>
				<div v-if="isRequired && !inline" class="cms_group_title_required_text">{{ t(`card.modifiers.required-text`) }}</div>
			</div>
		</header>

		<div v-drag-scroll class="cms_tiles" :class="tilesClass" :style="tilesStyle">
			<button
				v-for="tile in tiles"
				:key="tile.id"
				type="button"
				class="cms_tile"
				:class="{ cms_tile_selected: isSelected(tile.modifier) }"
				@click="selectTile($event, tile.modifier)"
			>
				<div class="cms_tile_image" :class="{ cms_tile_image_inline: inline }">
					<img v-if="!tile.video" :src="tile.image" :alt="tile.name" decoding="async">
					<video v-else loop autoplay muted v-muted playsinline class="cms_tile_video">
						<source :src="tile.video" type="video/webm">
					</video>
				</div>
				<p class="cms_tile_name">{{ tile.name }}</p>
				<p class="cms_tile_price" :class="{ cms_tile_price_free: tile.isFree }">{{ tile.priceText }}</p>

				<div v-if="(inline || tile.hasCounter) && isSelected(tile.modifier)" class="cms_tile_counter" @click="stopInlineCounterClick">
					<button type="button" class="cms_tile_btn" @click.stop.prevent="ctx.decrementModifier(tile.modifier, group)">−</button>
					<span class="cms_tile_count">{{ getAmount(tile.modifier) }}</span>
					<button type="button" class="cms_tile_btn" :disabled="!canIncrementTile(tile.modifier)" @click.stop.prevent="incrementTile($event, tile.modifier)">
						+
					</button>
				</div>
				<span v-else-if="inline" class="cms_tile_add">+</span>
			</button>
		</div>
	</section>
</template>

<style scoped lang="scss">
  @use './liquidModifiers.scss';
</style>
